import React, { useState } from "react";
import { toast } from "sonner";

import { runIntercompanyAction } from "@/api/intercompany";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ReconActionRequest, ReconActionResult } from "@/types/api";

export interface PendingAction {
  rowIndex: number;
  label: string;
  req: ReconActionRequest;
}

interface Props {
  pairId: string;
  actions: PendingAction[];
  onResult: (rowIndex: number, result: ReconActionResult) => void;
  onClose: () => void;
}

type Phase = "confirm" | "running" | "done";

export function ApproveBatchDialog({ pairId, actions, onResult, onClose }: Props): React.ReactElement {
  const [phase, setPhase] = useState<Phase>("confirm");
  const [doneCount, setDoneCount] = useState(0);
  const [okCount, setOkCount] = useState(0);
  const [errCount, setErrCount] = useState(0);

  const run = async () => {
    setPhase("running");
    let ok = 0;
    let err = 0;
    for (let i = 0; i < actions.length; i++) {
      const a = actions[i]!;
      try {
        const res = await runIntercompanyAction(pairId, a.req);
        onResult(a.rowIndex, res);
        if (res.ok) ok += 1;
        else err += 1;
      } catch (e: unknown) {
        const detail =
          (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail ?? String(e);
        onResult(a.rowIndex, {
          ok: false,
          kind: a.req.kind,
          message: String(detail),
          created_sf_ref_key: null,
          created_sf_number: null,
          posted: null,
          postuplenie_ref_key: a.req.postuplenie_ref_key ?? null,
          sf_received_ref_key: a.req.sf_received_ref_key ?? null,
        });
        err += 1;
      }
      setDoneCount(i + 1);
      setOkCount(ok);
      setErrCount(err);
    }
    setPhase("done");
    if (err === 0) toast.success(`Готово: успешно ${ok}`);
    else toast.warning(`Готово: успешно ${ok}, с ошибками ${err}`);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && phase !== "running") onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Подтвердите действия ({actions.length})</DialogTitle>
          <DialogDescription>
            Каждое действие выполняется в 1С и необратимо в полном смысле только для
            создания СФ; пометка на удаление — обратима (DeletionMark). История правок
            остаётся в 1С (автор + комментарий).
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-72 space-y-1 overflow-y-auto py-2 text-sm">
          {actions.map((a) => (
            <div key={a.rowIndex} className="rounded border px-2 py-1">
              {a.label}
            </div>
          ))}
        </div>

        {phase !== "confirm" && (
          <p className="text-sm text-muted-foreground">
            Выполнено {doneCount} из {actions.length} · успешно {okCount}
            {errCount > 0 ? ` · ошибок ${errCount}` : ""}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {phase === "confirm" && (
            <>
              <Button variant="outline" onClick={onClose}>Отмена</Button>
              <Button onClick={run}>Подтвердить и выполнить</Button>
            </>
          )}
          {phase === "running" && (
            <Button disabled>Выполняется…</Button>
          )}
          {phase === "done" && (
            <Button onClick={onClose}>Закрыть</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
